
import { supabase } from "@/integrations/supabase/client";
import { Technique, TechniquePosition, TechniqueType, TechniqueDifficulty } from "@/types/technique";
import { transformTechniqueFromDB } from "./types";
import type { Database } from '@/integrations/supabase/types';

type TechniqueUpdate = Database['public']['Tables']['techniques']['Update'];

export interface CreateTechniqueInput {
  name: string;
  description: string;
  positionId: string;
  techniquePosition: TechniquePosition;
  techniqueType: TechniqueType;
  difficulty: TechniqueDifficulty;
  imageUrl?: string;
  videoUrl?: string;
  keyPoints: string[];
  commonMistakes: string[];
  personalNotes?: string;
  category?: string;
  subcategory?: string;
  isPrivate?: boolean; // Add isPrivate field
}

// Create a new user technique
export const createUserTechnique = async (input: CreateTechniqueInput): Promise<Technique> => {
  try {
    // First, check if user is authenticated
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError || !userData.user) {
      throw new Error('You must be logged in to create techniques');
    }
    
    // Create slug from name
    const slug = input.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    
    // Find a valid position from the positions table
    const { data: positionsData, error: positionsError } = await supabase
      .from('positions')
      .select('id')
      .limit(1);
      
    if (positionsError) {
      console.error('Error fetching positions:', positionsError);
      throw new Error(`Failed to fetch valid position: ${positionsError.message}`);
    }
    
    // Use the first position ID from the database or generate a random UUID as fallback
    const positionId = positionsData && positionsData.length > 0 
      ? positionsData[0].id 
      : crypto.randomUUID();
    
    // Insert the technique
    const { data: technique, error } = await supabase
      .from('techniques')
      .insert({
        name: input.name,
        description: input.description,
        position_id: positionId, // Use the valid position ID
        technique_position: input.techniquePosition,
        technique_type: input.techniqueType,
        difficulty: input.difficulty,
        image_url: input.imageUrl || null,
        video_url: input.videoUrl || null,
        key_points: input.keyPoints,
        common_mistakes: input.commonMistakes,
        category: input.category || null,
        subcategory: input.subcategory || null,
        slug,
        is_user_created: true,
        created_by_user_id: userData.user.id,
        is_private: input.isPrivate ?? true // Use the isPrivate field with default to true
      })
      .select()
      .single();
      
    if (error) {
      console.error('Error creating technique:', error);
      throw new Error(`Failed to create technique: ${error.message}`);
    }
    
    // If personal notes were provided, also create an entry in user_techniques
    if (input.personalNotes) {
      const { error: notesError } = await supabase
        .from('user_techniques')
        .insert({
          user_id: userData.user.id,
          technique_id: technique.id,
          notes: input.personalNotes,
          is_favorite: false // Don't automatically favorite when adding notes
        });
        
      if (notesError) {
        console.error('Error saving personal notes:', notesError);
        // Don't throw here, as the technique was created successfully
      }
    }
    
    return transformTechniqueFromDB(technique);
    
  } catch (error: unknown) {
    console.error('Unexpected error in createUserTechnique:', error);
    throw error instanceof Error
      ? error
      : new Error('An unexpected error occurred while creating the technique');
  }
};

// Update an existing user technique
export const updateUserTechnique = async (
  techniqueId: string, 
  updates: Partial<CreateTechniqueInput>
): Promise<Technique> => {
  try {
    // First, check if user is authenticated
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError || !userData.user) {
      throw new Error('You must be logged in to update techniques');
    }
    
    // Prepare updates object
    const techniqueUpdates: TechniqueUpdate = {};
    
    if (updates.name) {
      techniqueUpdates.name = updates.name;
      techniqueUpdates.slug = updates.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    }
    
    if (updates.description) techniqueUpdates.description = updates.description;
    if (updates.positionId) techniqueUpdates.position_id = updates.positionId;
    if (updates.techniquePosition) techniqueUpdates.technique_position = updates.techniquePosition;
    if (updates.techniqueType) techniqueUpdates.technique_type = updates.techniqueType;
    if (updates.difficulty) techniqueUpdates.difficulty = updates.difficulty;
    if ('imageUrl' in updates) techniqueUpdates.image_url = updates.imageUrl || null;
    if ('videoUrl' in updates) techniqueUpdates.video_url = updates.videoUrl || null;
    if (updates.keyPoints) techniqueUpdates.key_points = updates.keyPoints;
    if (updates.commonMistakes) techniqueUpdates.common_mistakes = updates.commonMistakes;
    if (updates.category) techniqueUpdates.category = updates.category;
    if (updates.subcategory) techniqueUpdates.subcategory = updates.subcategory;
    
    // Update the technique
    const { data: technique, error } = await supabase
      .from('techniques')
      .update(techniqueUpdates)
      .eq('id', techniqueId)
      .eq('created_by_user_id', userData.user.id) // Ensure the user owns this technique
      .select()
      .single();
      
    if (error) {
      console.error('Error updating technique:', error);
      throw new Error(`Failed to update technique: ${error.message}`);
    }
    
    // If personal notes were provided, update or create entry in user_techniques
    if (updates.personalNotes !== undefined) {
      // Check if user_technique entry exists
      const { data: existingEntry } = await supabase
        .from('user_techniques')
        .select('id')
        .eq('user_id', userData.user.id)
        .eq('technique_id', techniqueId)
        .maybeSingle();
        
      if (existingEntry) {
        // Update existing entry
        const { error: notesError } = await supabase
          .from('user_techniques')
          .update({ notes: updates.personalNotes })
          .eq('id', existingEntry.id);
          
        if (notesError) {
          console.error('Error updating personal notes:', notesError);
        }
      } else {
        // Create new entry
        const { error: notesError } = await supabase
          .from('user_techniques')
          .insert({
            user_id: userData.user.id,
            technique_id: techniqueId,
            notes: updates.personalNotes,
            is_favorite: false // Don't automatically favorite when adding notes
          });
          
        if (notesError) {
          console.error('Error saving personal notes:', notesError);
        }
      }
    }
    
    return transformTechniqueFromDB(technique);
    
  } catch (error: unknown) {
    console.error('Unexpected error in updateUserTechnique:', error);
    throw error instanceof Error
      ? error
      : new Error('An unexpected error occurred while updating the technique');
  }
};

// Delete a user technique
export const deleteUserTechnique = async (techniqueId: string): Promise<void> => {
  try {
    // First, check if user is authenticated
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError || !userData.user) {
      throw new Error('You must be logged in to delete techniques');
    }
    
    // Delete any user_techniques entries first
    const { error: userTechniqueError } = await supabase
      .from('user_techniques')
      .delete()
      .eq('technique_id', techniqueId)
      .eq('user_id', userData.user.id);
      
    if (userTechniqueError) {
      console.error('Error deleting user_technique entry:', userTechniqueError);
      // Continue anyway to try to delete the technique
    }
    
    // Delete the technique
    const { error } = await supabase
      .from('techniques')
      .delete()
      .eq('id', techniqueId)
      .eq('created_by_user_id', userData.user.id); // Ensure the user owns this technique
      
    if (error) {
      console.error('Error deleting technique:', error);
      throw new Error(`Failed to delete technique: ${error.message}`);
    }
    
  } catch (error: unknown) {
    console.error('Unexpected error in deleteUserTechnique:', error);
    throw error instanceof Error
      ? error
      : new Error('An unexpected error occurred while deleting the technique');
  }
};
